import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

interface RequestDetails {
  productId: string;
  search: string;
  page: number;
  record: number;
  sortOrder: string;
  sortColumn: string;
}
interface SKU {
  SkuId: string;
  Name: string;
  Description: string;
  StatusCd: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  CreatedUser: string;
  ValidFrom: string;
  ValidTo: string;
}

interface APiData {
  Page: number;
  Record: number;
  TotalRecord: number;
  TotalPages: number;
  Result: SKU[];
}

interface SkuListState {
  isLoading: boolean;
  data: APiData | null;
  latestSkuList: APiData | null;
  error: string | null;
}

const initialData = {
  Page: 1,
  Record: 0,
  TotalRecord: 0,
  TotalPages: 0,
  Result: [],
};

const initialState: SkuListState = {
  isLoading: true,
  data: initialData,
  latestSkuList: initialData,
  error: null,
};

const mapSortColumn = (sortColumn: string) => {
  if (sortColumn === "SKU #") {
    sortColumn = "SkuId";
  } else if (sortColumn === "SKU Name") {
    sortColumn = "Name";
  } else if (sortColumn === "Created By") {
    sortColumn = "CreatedUser";
  } else if (sortColumn === "Created Date") {
    sortColumn = "CreatedDtm";
  } else if (sortColumn === "Updated Date") {
    sortColumn = "UpdatedDtm";
  } else if (sortColumn === "Status") {
    sortColumn = "StatusCd";
  } else if (sortColumn === "S.No.") {
    sortColumn = "serialNo";
  } else {
    sortColumn = "";
  }
  return sortColumn;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchSkuListData = createAsyncThunk<any, RequestDetails>(
  "CTDBSkuListSlice/fetchSkuListData",
  async (requestDetails, thunkApi) => {
    const sortOrder =  requestDetails.sortOrder === null || requestDetails.sortOrder === "" || requestDetails.sortOrder === undefined ? "DESC" : requestDetails.sortOrder.toUpperCase();
    try {
      const response = await httpClient.post("/db/product/sku", {
        ProductId: requestDetails.productId,
        Search: requestDetails.search,
        Page: requestDetails.page,
        Record: requestDetails.record,
        SortOrder: sortOrder,
        SortColumn: mapSortColumn(requestDetails.sortColumn) || "SkuId",
        Action: "retrieveAll",
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get product sku list",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get product sku list",
      });
    }
  }
);

export const fetchLatestSkuListData = createAsyncThunk<any, RequestDetails>(
  "CTDBSkuListSlice/fetchLatestSkuListData",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/sku", {
        ProductId: requestDetails.productId,
        Search: requestDetails.search,
        Page: requestDetails.page,
        Record: requestDetails.record,
        SortOrder: requestDetails.sortOrder.toUpperCase(),
        SortColumn: mapSortColumn(requestDetails.sortColumn) || "SkuId",
        Action: "retrieveAll",
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get product sku list",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get product sku list",
      });
    }
  }
);

const CTDBSkuListSlice = createSlice({
  name: "CTDBSkuListSlice",
  initialState,
  reducers: {
    resetSkuListSate: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSkuListData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchSkuListData.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        }
      )
      .addCase(
        fetchSkuListData.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message;
        }
      )
      .addCase(
        fetchLatestSkuListData.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.latestSkuList = action.payload.Data;
          } else if (action.payload.Status == "error") {
            state.latestSkuList = null;
          }
        }
      );
  },
});

export const { resetSkuListSate } = CTDBSkuListSlice.actions;

export default CTDBSkuListSlice.reducer;
